import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Character, type CharView } from './Character';
import type { P } from './motion';
import { SIZES } from './stage.shared';

/**
 * The gutter float: Mr. Clippy after B13 — sticky at 38% of the viewport in the right gutter (docked bottom-right
 * when the gutter is narrow), and the reader's (CHOREOGRAPHY.md "The float is the reader's"): draggable anywhere in
 * the viewport (viewport-fixed where dropped, clamped so ≥ 70% of him stays on screen, 1.04 scale while dragged, eyes
 * follow the pointer's direction, two decaying 4px bounces on release; touch after a 150ms hold so the page still
 * scrolls; the position lives for the load only), and hideable: a 20px ink "×" at his top-right on hover, tap or
 * focus (180ms fade); click or Escape = a 220ms fade-and-shrink and he is gone for the load, with no "show" control.
 * Neither exists before B13 (the float is hidden and inert until then).
 */
export function GutterFloat({ rail, narrow, view, levelRef, reduced, railBox, floatNode, onEyes, onDragging, onHide }: {
  rail: { left: number; top: number; height: number };
  narrow: boolean;
  view: CharView;
  levelRef: RefObject<number>;
  reduced: boolean;
  railBox: RefObject<HTMLDivElement | null>;
  floatNode: (el: HTMLDivElement | null) => void;
  onEyes: (dir: P | null) => void;
  onDragging: (active: boolean) => void;
  onHide: () => void;
}) {
  const size = narrow ? SIZES.clippy.dock : SIZES.clippy.frame;
  const [pos, setPos] = useState<P | null>(null); // viewport-fixed once dragged
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false); // the release: two decaying 4px bounces, 320ms
  const [chip, setChip] = useState(false); // shown after a tap (hover and focus show it through CSS)
  const [gone, setGone] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number; last: P; hold: number; start: P; id: number } | null>(null);
  const chipTimer = useRef(0);
  const live = !view.hidden && !gone;

  const clamp = useCallback((x: number, y: number): P => ({
    x: Math.min(window.innerWidth - size * 0.7, Math.max(-size * 0.3, x)),
    y: Math.min(window.innerHeight - size * 0.7, Math.max(-size * 0.3, y)),
  }), [size]);
  const startDrag = useCallback((e: PointerEvent | React.PointerEvent, from: { x: number; y: number }) => {
    const el = wrap.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.setPointerCapture(e.pointerId);
    drag.current = { dx: from.x - r.left, dy: from.y - r.top, last: from, hold: 0, start: from, id: e.pointerId };
    setPos(clamp(r.left, r.top));
    setDragging(true);
    onDragging(true);
  }, [clamp, onDragging]);
  const onPointerDown = (e: React.PointerEvent) => {
    if (!live || e.button !== 0) return;
    const from = { x: e.clientX, y: e.clientY };
    if (e.pointerType === 'touch') {
      // the drag starts after a 150ms hold, so a swipe still scrolls the page
      const hold = window.setTimeout(() => { startDrag(e, from); }, 150);
      drag.current = { dx: 0, dy: 0, last: from, hold, start: from, id: e.pointerId };
      setChip(true);
      window.clearTimeout(chipTimer.current);
      chipTimer.current = window.setTimeout(() => setChip(false), 3000);
      return;
    }
    startDrag(e, from);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const p = { x: e.clientX, y: e.clientY };
    if (d.hold) { if (Math.hypot(p.x - d.start.x, p.y - d.start.y) > 6) { window.clearTimeout(d.hold); drag.current = null; } return; }
    setPos(clamp(p.x - d.dx, p.y - d.dy));
    const vx = p.x - d.last.x; const vy = p.y - d.last.y;
    if (Math.hypot(vx, vy) > 2) { const n = Math.hypot(vx, vy); onEyes({ x: vx / n, y: vy / n }); d.last = p; }
  };
  const endDrag = () => {
    const d = drag.current;
    if (!d) return;
    if (d.hold) { window.clearTimeout(d.hold); drag.current = null; return; }
    drag.current = null;
    setDragging(false);
    setSettling(true);
    window.setTimeout(() => setSettling(false), 320);
    onDragging(false);
    onEyes(null);
  };
  const hide = () => { if (!live) return; setGone(true); if (reduced) onHide(); };
  useEffect(() => () => { window.clearTimeout(chipTimer.current); }, []);
  const sticky = pos
    ? { position: 'fixed' as const, left: pos.x, top: pos.y, width: size, height: size, marginLeft: 0 }
    : { width: size, height: size, marginLeft: narrow ? 0 : -SIZES.clippy.frame / 2, top: narrow ? 'auto' : `calc(38vh - ${SIZES.clippy.frame / 2}px)` };
  return (
    <div className={`fx-gutter-rail${narrow ? ' dock' : ''}${pos ? ' free' : ''}`} style={{ left: rail.left, top: rail.top, height: rail.height }}>
      <div className="fx-gutter-sticky" ref={railBox} style={sticky}>
        <div
          ref={wrap}
          className={`fx-float-wrap${live ? ' live' : ''}${dragging ? ' dragging' : ''}${settling ? ' settling' : ''}${chip ? ' chip' : ''}${gone ? ' gone' : ''}`}
          style={{ width: size, height: size }}
          tabIndex={live ? 0 : -1}
          role="img"
          aria-label="Mr. Clippy, the buyer, floating beside the page. Drag him anywhere; Escape hides him."
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(e) => { if (e.key === 'Escape') hide(); }}
          onAnimationEnd={(e) => { if (gone && e.animationName === 'fx-float-gone') onHide(); }}
        >
          <Character who="clippy" extraClass="fx-float" view={view} levelRef={levelRef} reduced={reduced} ref={floatNode} />
          {live && (
            <button type="button" className="fx-float-hide" aria-label="Hide Mr. Clippy for this visit" onPointerDown={(e) => e.stopPropagation()} onClick={hide}>
              ×
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
